import {
  useMutation,
  UseMutationResult,
  useQuery,
} from "@tanstack/react-query";
import { queryKeys } from "../constants";

import { handleAPIError, queryClient } from "utils";
import {
  createExpense,
  deleteExpense,
  getExpenseById,
  getExpenses,
  updateExpense,
} from "services";
import { ExpenseType } from "types";
import { toast } from "react-toastify";

// Fetch all expenses
export const useGetExpenses = () => {
  return useQuery({
    queryKey: [queryKeys.expenses],
    queryFn: async () => await getExpenses(),
  });
};

// Fetch a single expense by ID
export const useGetExpenseById = (id: string) => {
  return useQuery({
    queryKey: [queryKeys.expenses, id],
    queryFn: async () => await getExpenseById(id),
    enabled: !!id,
  });
};

// Create a new expense
export const useCreateExpense = () => {
  return useMutation({
    mutationFn: createExpense,
    onSuccess: async () => {
      // Invalidate expenses query on success to refresh the list
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.expenses],
      });
    },
  });
};

// Define the UpdateExpensePayload type
export type UpdateExpensePayload = {
  id: string; // Required ID
  data: ExpenseType;
};

// Hook to update a expense
export const useUpdateExpense = (): UseMutationResult<
  any,
  Error,
  UpdateExpensePayload
> => {
  return useMutation<any, Error, UpdateExpensePayload>({
    mutationFn: async ({ id, data }) => {
      // Make sure that updateExpense is defined to accept id and an object with expense details
      return updateExpense(id, data);
    },
    onSuccess: async () => {
      // Invalidate expenses query on success
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.expenses],
      });
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};

// Delete a expense
export const useDeleteExpense = () => {
  return useMutation({
    mutationFn: deleteExpense,
    onSuccess: async () => {
      // Invalidate expenses query on success
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.expenses],
      });
      toast.success("Expense deleted successfully.");
    },
    onError: (error) => {
      handleAPIError(error, true);
    },
  });
};
